import { useAction, useAtom } from "@reatom/npm-react";
import { useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useRedirectUrl } from "src/hooks";
import { isAuthedAtom } from "src/store/atoms/authAtom";
import { getMeRequest, isAdminAtom } from "src/store/fetchers";

import { RoutesPaths } from "./RoutesPaths";

// Same-origin paths served outside this SPA (e.g. export-api's Bull Board at
// /bull/ui). Returning to them after login needs a full navigation — a
// client-side <Navigate> would land on the SPA's NotFound.
const EXTERNAL_REDIRECT_PREFIXES = [
  "/bull/",
  "/translator",
  "/api/sso/oidc-interaction/",
];

const isExternalRedirect = (url: string | null): url is string =>
  !!url && EXTERNAL_REDIRECT_PREFIXES.some((prefix) => url.startsWith(prefix));

export const ProtectedRoute = () => {
  const { setRedirectUrl } = useRedirectUrl();
  const [isAuthed] = useAtom(isAuthedAtom);

  if (!isAuthed) {
    return (
      <Navigate to={setRedirectUrl(RoutesPaths.AUTH.LOGIN)} replace={true} />
    );
  }
  return <Outlet />;
};

export const AdminProtectedRoute = () => {
  const [isAuthed] = useAtom(isAuthedAtom);
  const getMe = useAction(getMeRequest);
  const [me] = useAtom(getMeRequest.dataAtom);
  const [isUserLoading] = useAtom(getMeRequest.isLoadingAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  useEffect(() => {
    if (isAuthed) getMe();
  }, [isAuthed, getMe]);

  if (isUserLoading || !me) return "loading...";

  if (!isAuthed) {
    return <ProtectedRoute />;
  }

  if (!isAdmin) {
    return <Navigate to={RoutesPaths.LK.BASE_PATH} replace={true} />;
  }
  return <Outlet />;
};

export const NonAuthProtectedRoute = () => {
  const { redirectUrl } = useRedirectUrl();
  const [isAuthed] = useAtom(isAuthedAtom);
  const location = useLocation();

  const hasDisabledPaths = useMemo(
    () =>
      [RoutesPaths.AUTH.VERIFY, RoutesPaths.AUTH.FINALY_REG].includes(
        location.pathname,
      ),
    [location.pathname],
  );

  const shouldRedirect = isAuthed && !hasDisabledPaths;
  const externalRedirectUrl =
    shouldRedirect && isExternalRedirect(redirectUrl) ? redirectUrl : null;

  useEffect(() => {
    if (externalRedirectUrl) window.location.assign(externalRedirectUrl);
  }, [externalRedirectUrl]);

  if (externalRedirectUrl) return null;

  if (shouldRedirect) {
    return (
      <Navigate to={redirectUrl ?? RoutesPaths.LK.BASE_PATH} replace={true} />
    );
  }
  return <Outlet />;
};
